/**
 * Group evaluation: the headline rate, the trend, and where the variation comes from.
 *
 * Only sessions that have already happened are counted, and slots nobody recorded stay out of
 * every rate's denominator — a half-filled roll call must not read as a bad week.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { weekdayOf } from '../../shared/dates.js';
import {
  accumulate,
  emptyCounts,
  formatRate,
  presentRate,
  recordedCount,
  type SessionTally,
  type StateCounts,
} from '../../shared/stats.js';
import type { Weekday } from '../../shared/types.js';
import { setCountExcusedAsPresent } from '../data/mutations.js';
import { useStore } from '../data/context.js';
import { useProjectDerived } from '../data/derived.js';
import { BarRows, type BarRow } from '../ui/charts/BarRows.js';
import { RateLine } from '../ui/charts/RateLine.js';
import { StatusBar } from '../ui/charts/StatusBar.js';
import { StateFigures, StateLegend } from '../ui/attendance.js';
import { formatMediumDate, plural } from '../ui/format.js';
import { EmptyState, ToggleRow } from '../ui/primitives.js';
import { ProjectFrame } from '../ui/Shell.js';

/** Weekday order used by the weekday breakdown, Monday first. */
const WEEK_ORDER: Weekday[] = [1, 2, 3, 4, 5, 6, 0];

/** A reference Sunday, used to name weekdays in the viewer's locale. */
const REFERENCE_SUNDAY = Date.UTC(2024, 0, 7);

/** Returns the localized long name of a weekday. */
function weekdayName(weekday: Weekday): string {
  return new Date(REFERENCE_SUNDAY + weekday * 86_400_000)
    .toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' });
}

/** Groups session tallies by weekday, keeping only weekdays that actually had sessions. */
function tallyWeekdays(
  tallies: readonly SessionTally[],
  countExcusedAsPresent: boolean,
): { weekday: Weekday; counts: StateCounts; rate: number | null; sessions: number }[] {
  const byWeekday = new Map<Weekday, { counts: StateCounts; sessions: number }>();
  for (const tally of tallies) {
    const weekday = weekdayOf(tally.date);
    const bucket = byWeekday.get(weekday) ?? { counts: emptyCounts(), sessions: 0 };
    accumulate(bucket.counts, tally.counts);
    bucket.sessions += 1;
    byWeekday.set(weekday, bucket);
  }
  return WEEK_ORDER.flatMap((weekday) => {
    const bucket = byWeekday.get(weekday);
    if (bucket === undefined) return [];
    return [{
      weekday,
      counts: bucket.counts,
      rate: presentRate(bucket.counts, countExcusedAsPresent),
      sessions: bucket.sessions,
    }];
  });
}

/**
 * A figure in the summary strip: a number and what it counts.
 *
 * Headed by a rule rather than boxed, so the three read as one row of figures and match the
 * state figures above them.
 */
function Figure({ value, label }: { value: string; label: string }): ReactNode {
  return (
    <div className="border-t-2 border-line-strong pt-2">
      <p className="tabular text-display leading-none font-bold">{value}</p>
      <p className="mt-1.5 text-tiny text-ink-2">{label}</p>
    </div>
  );
}

/** Renders the group statistics. */
export function StatsPage(): ReactNode {
  const { projectId } = useParams();
  const store = useStore();
  const derived = useProjectDerived(projectId);
  const [showNumbers, setShowNumbers] = useState(false);

  const countExcused = derived?.project.countExcusedAsPresent ?? false;

  const weekdayTallies = useMemo(
    () => (derived === null ? [] : tallyWeekdays(derived.sessionTallies, countExcused)),
    [derived, countExcused],
  );
  const personRows = useMemo((): BarRow[] => {
    if (derived === null) return [];
    return [...derived.personTallies]
      .sort((left, right) => {
        if (left.rate === null && right.rate === null) return 0;
        if (left.rate === null) return 1;
        if (right.rate === null) return -1;
        return left.rate - right.rate;
      })
      .map((tally): BarRow => ({
        id: tally.person.id,
        label: tally.person.name,
        value: tally.rate,
        display: formatRate(tally.rate),
        to: `/p/${derived.project.id}/people/${tally.person.id}`,
        ...(tally.missStreak >= 2
          ? { note: { text: `${tally.missStreak} missed in a row`, tone: 'warning' as const } }
          : {}),
      }));
  }, [derived]);

  if (derived === null) {
    return <ProjectFrame><EmptyState message="This project is not on this device." /></ProjectFrame>;
  }

  const { project, summary, sessionTallies, pastSessions: sessions } = derived;

  if (sessions.length === 0) {
    return (
      <ProjectFrame>
        <EmptyState message="No sessions have happened yet, so there is nothing to evaluate. Set the session days in the calendar first." />
      </ProjectFrame>
    );
  }

  const recorded = recordedCount(summary.counts);
  const weekdayRows: BarRow[] = weekdayTallies.map((entry) => ({
    id: String(entry.weekday),
    label: weekdayName(entry.weekday),
    value: entry.rate,
    display: formatRate(entry.rate),
    note: { text: `${entry.sessions} ${plural(entry.sessions, 'session', 'sessions')}`, tone: 'muted' as const },
  }));

  return (
    <ProjectFrame>
      <section className="px-4 pt-6 pb-5">
        <p className="tabular text-hero font-bold text-brand-text">{formatRate(summary.rate)}</p>
        <p className="mt-1 max-w-prose text-ink-2">
          present across {summary.sessionCount} {plural(summary.sessionCount, 'session', 'sessions')} and{' '}
          {recorded} recorded {plural(recorded, 'mark', 'marks')}
          {countExcused ? ', counting excused absences as present' : ', excused absences counted as absent'}.
        </p>
        <div className="mt-5"><StatusBar counts={summary.counts} height={10} /></div>
        <div className="mt-3"><StateFigures counts={summary.counts} /></div>
      </section>

      <section className="grid grid-cols-3 gap-4 border-y border-line px-4 py-5">
        <Figure value={String(summary.sessionCount)} label={plural(summary.sessionCount, 'session so far', 'sessions so far')} />
        <Figure value={String(summary.rosterSize)} label={plural(summary.rosterSize, 'person tracked', 'people tracked')} />
        <Figure value={formatRate(summary.coverage)} label="of slots recorded" />
      </section>

      <section className="border-b border-line px-4 py-2">
        <ToggleRow
          label="Count excused absences as present"
          description="Changes every rate in this project, including each person's page."
          checked={countExcused}
          onChange={(value) => void setCountExcusedAsPresent(store, project.id, value)}
        />
      </section>

      <section className="px-4 py-6">
        <figure className="m-0">
          <RateLine tallies={sessionTallies} />
          <figcaption className="mt-2 text-tiny text-ink-2">
            Present rate per session. Sessions with nothing recorded are left out.
          </figcaption>
        </figure>
        <button
          type="button"
          className="mt-3 text-tiny font-semibold text-brand-text"
          aria-expanded={showNumbers}
          onClick={() => setShowNumbers(!showNumbers)}
        >
          {showNumbers ? 'Hide the numbers' : 'Show the numbers'}
        </button>
        {/*
          * The chart's data as text. Three columns rather than one per state: seven numeric
          * columns cannot fit a phone, and a table that has to be scrolled sideways to be read
          * is not a table anyone reads. The bar carries the split, each session links to its
          * own roll call, and that is where the five figures belong.
          */}
        {showNumbers ? (
          <div className="mt-3 max-h-80 overflow-y-auto">
            <table className="w-full border-collapse text-tiny">
              <thead>
                <tr className="border-b border-line text-left text-ink-2">
                  <th scope="col" className="py-2 pr-3 font-semibold">Session</th>
                  <th scope="col" className="py-2 pr-3 text-right font-semibold">Present</th>
                  <th scope="col" className="py-2 font-semibold">Split</th>
                </tr>
              </thead>
              <tbody>
                {[...sessionTallies].reverse().map((tally) => (
                  <tr key={tally.date} className="border-b border-line">
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <Link
                        to={`/p/${project.id}?date=${tally.date}`}
                        className="font-medium text-brand-text underline-offset-2 hover:underline"
                      >
                        {formatMediumDate(tally.date)}
                      </Link>
                    </td>
                    <td className="tabular py-2 pr-3 text-right font-semibold">{formatRate(tally.rate)}</td>
                    <td className="w-full py-2"><StatusBar counts={tally.counts} height={6} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="py-3"><StateLegend /></div>
          </div>
        ) : null}
      </section>

      {weekdayRows.length > 1 ? (
        <section className="border-t border-line bg-surface">
          <BarRows rows={weekdayRows} caption="Present rate by weekday, pooled across every session." />
        </section>
      ) : null}

      <section className="border-t border-line bg-surface">
        <BarRows
          rows={personRows}
          caption="Present rate per person, lowest first."
        />
      </section>
    </ProjectFrame>
  );
}
